// Module 19 (BL-380) G8 - Events Settings: the values, and how the event form, the guest form and the import use them.
// Defaults are admin-configurable, not constants: when an organization has saved nothing, DEFAULT_EVENT_SETTINGS applies and every
// screen behaves exactly as it did before G8. Each event can override a default; changing it on one event never changes the setting.
import { PLUS_ONES_MAX } from './guests/guestConstants';
import { checklistNotes } from './eventConstants';
import { DEFAULT_CREW_WORD, DEFAULT_EVENT_WORD, WORD_PATTERN, WORD_RULE, cleanWord } from './eventWords';

export const SETTINGS_SUBTITLE = 'Defaults for every new event. Each event can override them.';
// Q-046: the same sentence with the company's word ("Defaults for every new party. Each party can override them.").
export function settingsSubtitle(words) {
    const one = words?.eventLower || 'event';
    return `Defaults for every new ${one}. Each ${one} can override them.`;
}

// What the screens use when nothing is saved. Times are 'HH:MM' strings, blank = no default.
export const DEFAULT_EVENT_SETTINGS = Object.freeze({
    default_start_time: '',
    default_end_time: '',
    default_event_type: '',
    default_expected_guest_count: '',
    default_plus_ones_allowed: 0,
    allow_plus_ones: true,
    allow_maybe: true,
    ask_meal_choice: false,
    ask_dietary: false,
    rsvp_deadline_days: 21,
    // Q-046: the words the company's screens use (migration 240)
    event_word: DEFAULT_EVENT_WORD,
    crew_word: DEFAULT_CREW_WORD,
    // G6: Events Settings > Hotel & Travel (migration 241). On unless turned off.
    hotel_block_enabled: true,
    shuttle_enabled: true,
    // G5: Events Settings > Check-In (migration 246). On unless turned off; QR off = check guests in from the list only.
    checkin_enabled: true,
    checkin_qr_enabled: true,
});

export const SETTINGS_COLUMNS = 'org_id, default_start_time, default_end_time, default_event_type, default_expected_guest_count, default_plus_ones_allowed, allow_plus_ones, allow_maybe, ask_meal_choice, ask_dietary, rsvp_deadline_days, updated_at';
// Q-046 (migration 240). Read separately so the screens still work in the minutes between the code going live and the migration applying.
export const WORD_COLUMNS = 'event_word, crew_word';
// G6 (migration 241), read the same way.
export const TRAVEL_COLUMNS = 'hotel_block_enabled, shuttle_enabled';
export const OPTIONAL_COLUMNS = `${WORD_COLUMNS}, ${TRAVEL_COLUMNS}`;
export const OPTIONAL_KEYS = ['event_word', 'crew_word', 'hotel_block_enabled', 'shuttle_enabled'];
// G5 (migration 246), one step newer again: read and saved with a fallback of its own, so the screens work before 246 is applied.
export const CHECKIN_COLUMNS = 'checkin_enabled, checkin_qr_enabled';
export const CHECKIN_KEYS = ['checkin_enabled', 'checkin_qr_enabled'];

const hhmm = (value) => (value ? String(value).slice(0, 5) : '');

// A database row (or nothing) -> the values the screens use.
export function settingsFromRow(row) {
    if (!row) return { ...DEFAULT_EVENT_SETTINGS };
    return {
        default_start_time: hhmm(row.default_start_time),
        default_end_time: hhmm(row.default_end_time),
        default_event_type: row.default_event_type || '',
        default_expected_guest_count: row.default_expected_guest_count === null || row.default_expected_guest_count === undefined ? '' : String(row.default_expected_guest_count),
        default_plus_ones_allowed: Number.isFinite(Number(row.default_plus_ones_allowed)) ? Number(row.default_plus_ones_allowed) : DEFAULT_EVENT_SETTINGS.default_plus_ones_allowed,
        allow_plus_ones: row.allow_plus_ones !== false,
        allow_maybe: row.allow_maybe !== false,
        ask_meal_choice: row.ask_meal_choice === true,
        ask_dietary: row.ask_dietary === true,
        rsvp_deadline_days: Number.isFinite(Number(row.rsvp_deadline_days)) ? Number(row.rsvp_deadline_days) : DEFAULT_EVENT_SETTINGS.rsvp_deadline_days,
        event_word: cleanWord(row.event_word, DEFAULT_EVENT_WORD),
        crew_word: cleanWord(row.crew_word, DEFAULT_CREW_WORD),
        hotel_block_enabled: row.hotel_block_enabled !== false,
        shuttle_enabled: row.shuttle_enabled !== false,
        checkin_enabled: row.checkin_enabled !== false,
        checkin_qr_enabled: row.checkin_qr_enabled !== false,
    };
}

// The screen's values -> typed database columns (blank means "no default").
export function settingsToRow(values) {
    const guests = String(values.default_expected_guest_count ?? '').trim();
    return {
        default_start_time: values.default_start_time || null,
        default_end_time: values.default_end_time || null,
        default_event_type: values.default_event_type || null,
        default_expected_guest_count: guests === '' ? null : Number(guests),
        default_plus_ones_allowed: Number(values.default_plus_ones_allowed || 0),
        allow_plus_ones: values.allow_plus_ones !== false,
        allow_maybe: values.allow_maybe !== false,
        ask_meal_choice: values.ask_meal_choice === true,
        ask_dietary: values.ask_dietary === true,
        rsvp_deadline_days: Number(values.rsvp_deadline_days ?? DEFAULT_EVENT_SETTINGS.rsvp_deadline_days),
        event_word: String(values.event_word ?? DEFAULT_EVENT_WORD).trim(),
        crew_word: String(values.crew_word ?? DEFAULT_CREW_WORD).trim(),
        hotel_block_enabled: values.hotel_block_enabled !== false,
        shuttle_enabled: values.shuttle_enabled !== false,
        checkin_enabled: values.checkin_enabled !== false,
        checkin_qr_enabled: values.checkin_qr_enabled !== false,
    };
}

// Plain-English problem with the values, or '' when they can be saved.
export function settingsProblem(values) {
    const guests = String(values.default_expected_guest_count ?? '').trim();
    if (guests !== '' && (!/^\d{1,6}$/.test(guests))) return 'Default guests expected must be a whole number, or blank.';
    const plus = Number(values.default_plus_ones_allowed);
    if (!Number.isInteger(plus) || plus < 0 || plus > PLUS_ONES_MAX) return `Default plus-ones must be a whole number from 0 to ${PLUS_ONES_MAX}.`;
    const days = Number(values.rsvp_deadline_days);
    if (!Number.isInteger(days) || days < 0 || days > 365) return 'The RSVP deadline must be a whole number of days from 0 to 365.';
    if (!WORD_PATTERN.test(String(values.event_word ?? DEFAULT_EVENT_WORD).trim())) return `The word for an event: ${WORD_RULE}`;
    if (!WORD_PATTERN.test(String(values.crew_word ?? DEFAULT_CREW_WORD).trim())) return `The word for a crew member: ${WORD_RULE}`;
    return '';
}

// A new event starts from the defaults. `kit` is the starter type picked from the "+ New event" menu (it wins over the default type).
// An event type that is no longer offered (turned off or removed) is ignored rather than applied.
export function newEventForm(blank, settings, kit, kits = []) {
    const chosen = kit || kits.find((item) => item.key === settings.default_event_type) || null;
    return {
        ...blank,
        start_time: settings.default_start_time || blank.start_time,
        end_time: settings.default_end_time || blank.end_time,
        expected_guest_count: settings.default_expected_guest_count !== '' ? String(settings.default_expected_guest_count) : blank.expected_guest_count,
        event_type: chosen?.key || blank.event_type,
        notes: chosen ? checklistNotes(chosen) : blank.notes,
    };
}

// A new guest starts with the default plus-ones (0 when plus-ones are turned off).
export function plusOnesDefault(settings) {
    return settings.allow_plus_ones ? Math.min(Math.max(Number(settings.default_plus_ones_allowed) || 0, 0), PLUS_ONES_MAX) : 0;
}

// RSVP choices on the guest screen. "Maybe" is hidden when it is turned off, except for a guest who already has it (their status is kept).
export function rsvpChoices(allStatuses, settings, current = '') {
    return allStatuses.filter((status) => status !== 'maybe' || settings.allow_maybe || current === 'maybe');
}

// What the Event Types tile and the starter-kit rules need.
export const STARTER_KIT_KEYS = ['wedding', 'corporate', 'social', 'nonprofit_gala'];
const KIT_ICONS = { wedding: '💍', corporate: '💼', social: '🎉', nonprofit_gala: '🎗️' };
export const DEFAULT_KIT_ICON = '🗓️';
export function kitIcon(key) {
    return KIT_ICONS[key] || DEFAULT_KIT_ICON;
}

// 'Bar mitzvah!' -> 'bar_mitzvah' (the database key is 1 to 40 of a-z 0-9 _); a taken key gets _2, _3 ...
export function kitKeyFromLabel(label, takenKeys = []) {
    const base = String(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 36) || 'type';
    const taken = new Set(takenKeys);
    if (!taken.has(base)) return base;
    for (let n = 2; n < 1000; n += 1) {
        const key = `${base}_${n}`;
        if (!taken.has(key)) return key;
    }
    return `${base}_${Date.now() % 100000}`;
}

// The checklist is edited as one task per line.
export function checklistFromText(text) {
    return String(text || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean).slice(0, 100);
}
export function checklistToText(list) {
    return (Array.isArray(list) ? list : []).join('\n');
}
